import {
  DeleteOutline,
  ExpandMore,
  OpenInNew,
  WarningAmber,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const SECTION_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: '20px',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15))',
  px: 2.5,
  py: 2.5,
  mb: 2,
};

interface DangerZoneSectionProps {
  userEmail: string;
  deletingAccount: boolean;
  onDeleteAccount: () => void;
}

const DangerZoneSection: React.FC<DangerZoneSectionProps> = ({
  userEmail,
  deletingAccount,
  onDeleteAccount,
}) => {
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [expanded, setExpanded] = useState(false);

  const isDeleteEnabled =
    deleteConfirmEmail.trim().toLowerCase() ===
    userEmail.trim().toLowerCase();

  return (
    <Box
      sx={{
        ...SECTION_SX,
        border: '2px solid #E64646',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((current) => !current);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
        }}
      >
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse Danger Zone' : 'Expand Danger Zone'}
          sx={{
            color: '#E64646',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 160ms ease',
          }}
        >
          <ExpandMore />
        </IconButton>
        <WarningAmber sx={{ color: '#E64646', fontSize: 24 }} />
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            fontSize: 18,
            color: '#E64646',
          }}
        >
          Danger Zone
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5 }}>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#666',
              mb: 2,
            }}
          >
            These actions are permanent and cannot be undone.
          </Typography>

          <Button
            variant="outlined"
            component="a"
            href="https://jeffsays.com/data-deletion.html"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNew />}
            sx={{
              color: '#E64646',
              borderColor: '#E64646',
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              mb: 2,
              '&:hover': {
                borderColor: '#CC0000',
                bgcolor: 'rgba(230, 70, 70, 0.05)',
              },
            }}
          >
            Data Deletion Request
          </Button>

          <Divider sx={{ mb: 2 }} />

          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              fontSize: 15,
              color: '#E64646',
              mb: 1,
            }}
          >
            Delete Account
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#666',
              mb: 1.5,
            }}
          >
            Deleting your account will disable Jeff Says immediately and stop
            AI processing. We will begin deleting your data within 7 days, with
            backups removed within 30 days. This cannot be undone.
          </Typography>
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              color: '#333',
              fontWeight: 600,
              mb: 1,
            }}
          >
            Type your email address to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={userEmail || 'your@email.com'}
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: FONT_FAMILY,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<DeleteOutline />}
            onClick={onDeleteAccount}
            disabled={!isDeleteEnabled || deletingAccount}
            sx={{
              bgcolor: '#E64646',
              color: '#FFFFFF',
              fontFamily: FONT_FAMILY,
              fontWeight: 700,
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: '#CC0000' },
              '&.Mui-disabled': {
                bgcolor: '#F5C6C6',
                color: '#FFFFFF',
              },
            }}
          >
            {deletingAccount ? 'Deleting…' : 'Permanently Delete Account'}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default DangerZoneSection;
